import "expose-loader?jQuery!jquery";
import "expose-loader?$!jquery";
import "signalr";

import { Configuration } from "@/services/Configuration";
import { EventEmitter } from "events";

import CertificateWarningsViewModel from "@/models/security/zorgID/certificateWarningsViewModel";
import CreateSessionResultModel from "@/models/security/zorgID/createSessionResultModel";
import SessionOpenedViewModel from "@/models/security/zorgID/sessionOpenedViewModel";
import SecureElementViewModel from "@/models/security/zorgID/secureElementViewModel";
import SessionClosedViewModel from "@/models/security/zorgID/sessionClosedViewModel";

export default class ZorgIDHubService {
  private hubProxy!: SignalR.Hub.Proxy;

  private emitter: EventEmitter = new EventEmitter();
  private connection!: SignalR.Hub.Connection;

  private readonly connectionEstablishedEventName = "connectionEstablished";

  private signalRStarted: boolean = false;

  private hubName: string = "ZorgIDHub";

  constructor() {}

  public subscribeConnectionEstablished(
    method: (connectionEstablished: boolean) => void,
  ): void {
    this.emitter.on(this.connectionEstablishedEventName, method);
  }

  private registerOnServerEvents(): void {
    this.registerOnServerBindedClientInstanceGuidToZorgIDSessionIdEvent();
    this.registerOnServerKnownSessionEvent();
    this.registerOnServerCertificateWarningsEvent();
    this.registerOnServerSessionInitializedEvent();
    this.registerOnServerSessionOpenedEvent();
    this.registerOnServerRetrieveSecureElementEvent();
    this.registerOnServerSessionClosedEvent();
  }

  public startSignalR(): void {
    console.log(
      `Going to start a SignalR hub connection on url: '${
        Configuration.SignalRUrl
      }' for hub '${this.hubName}'`,
    );

    this.signalRStarted = true;

    this.connection = $.hubConnection(Configuration.SignalRUrl);
    this.hubProxy = this.connection.createHubProxy(this.hubName);

    this.registerOnServerEvents();
    this.startConnection();

    // Do not make changes to the code below!!!
    this.hubProxy.connection.reconnected(() => {
      // Handle reconnect. The default connection timeout is 30 secs so potentially we have missed a window of 30 secs
      console.log(`SignalR reconnected. Hub: '${this.hubName}'`);

      // SignalR is not available, if we immediately emit connectionEstablished
      setTimeout(() => {
        this.emitter.emit(this.connectionEstablishedEventName, true);
      }, 2000);
    });

    this.hubProxy.connection.disconnected(() => {
      if (this.signalRStarted) {
        console.log("Disconnected from ZorgId hub, we will try to reconnect");

        setTimeout(() => {
          console.log("Trying to reopen the ZorgId hub connection");

          this.startConnection();
        }, 5000); // Restart connection after 5 seconds.
      } else {
        console.log(
          `SignalR disconnected from hub connection on url: '${
            Configuration.SignalRUrl
          }' for hub '${this.hubName}'`,
        );
      }
    });
  }

  public stopSignalR(): void {
    this.signalRStarted = false;
    this.connection.stop();

    console.log(
      `SignalR Hub stopped, url: '${Configuration.SignalRUrl}', hub: '${
        this.hubName
      }'`,
    );
  }

  private startConnection(): void {
    const me: ZorgIDHubService = this;

    this.connection
      .start({})
      .done(data => me.handleStarted(data))
      .fail(error => me.handleError(error));
  }

  private handleStarted(data: any): void {
    console.log(
      `SignalR Hub connected, url: '${Configuration.SignalRUrl}', hub: '${
        this.hubName
      }' connection id: '${this.connection.id}'`,
    );

    this.emitter.emit(this.connectionEstablishedEventName, true);
  }

  private handleError(err: any): void {
    console.log("Error while establishing connection");
    console.log(err);

    this.emitter.emit(this.connectionEstablishedEventName, false);
  }

  //#region registerClientInstanceGuid

  public bindClientInstanceGuidToZorgIDSessionId(
    clientInstanceGuid: string,
    sessionId: string,
  ): void {
    this.hubProxy.invoke(
      "BindClientInstanceGuidToZorgIDSessionId",
      clientInstanceGuid,
      sessionId === undefined || sessionId === null || sessionId === ""
        ? null
        : sessionId,
    );
  }

  private readonly bindedClientInstanceGuidToZorgIDSessionIdReceivedEventName =
    "BindedClientInstanceGuidToZorgIDSessionId";

  public subscribeBindedClientInstanceGuidToZorgIDSessionIdReceived(
    method: (model: boolean) => void,
  ) {
    this.emitter.on(
      this.bindedClientInstanceGuidToZorgIDSessionIdReceivedEventName,
      method,
    );
  }

  private registerOnServerBindedClientInstanceGuidToZorgIDSessionIdEvent(): void {
    this.hubProxy.on(
      "BindedClientInstanceGuidToZorgIDSessionId",
      (model: boolean) => {
        this.emitter.emit(
          this.bindedClientInstanceGuidToZorgIDSessionIdReceivedEventName,
          model,
        );
      },
    );
  }

  ////#endregion

  //#region knownSession

  public knownSession(sessionId: string): void {
    this.hubProxy.invoke("KnownSession", sessionId);
  }

  private readonly knownSessionReceivedEventName = "KnownSession";

  public subscribeKnownSessionReceived(method: (model: boolean) => void) {
    this.emitter.on(this.knownSessionReceivedEventName, method);
  }

  private registerOnServerKnownSessionEvent(): void {
    this.hubProxy.on("KnownSession", (model: boolean) => {
      this.emitter.emit(this.knownSessionReceivedEventName, model);
    });
  }

  //#endregion

  //#region CertificateWarnings

  private registerOnServerCertificateWarningsEvent(): void {
    this.hubProxy.on(
      "CertificateWarnings",
      (model: CertificateWarningsViewModel) => {
        this.emitter.emit(this.certificateWarningsEventName, model);
      },
    );
  }

  private readonly certificateWarningsEventName = "CertificateWarnings";

  public subscribeCertificateWarningsReceived(
    method: (model: CertificateWarningsViewModel) => void,
  ) {
    this.emitter.on(this.certificateWarningsEventName, method);
  }

  //#endregion

  //#region initializeSession
  public initializeSession(clientInstanceGuid: string): void {
    this.hubProxy.invoke("InitializeSession", clientInstanceGuid);
  }

  private readonly sessionInitializedReceivedEventName = "SessionInitialized";

  public subscribeSessionInitializedReceived(
    method: (model: CreateSessionResultModel) => void,
  ) {
    this.emitter.on(this.sessionInitializedReceivedEventName, method);
  }

  private registerOnServerSessionInitializedEvent(): void {
    this.hubProxy.on(
      "SessionInitialized",
      (model: CreateSessionResultModel) => {
        this.emitter.emit(this.sessionInitializedReceivedEventName, model);
      },
    );
  }

  //#endregion

  //#region openedSession

  private readonly sessionOpenedReceivedEventName = "SessionOpened";

  public subscribeSessionOpenedReceived(
    method: (model: SessionOpenedViewModel) => void,
  ) {
    this.emitter.on(this.sessionOpenedReceivedEventName, method);
  }

  private registerOnServerSessionOpenedEvent(): void {
    this.hubProxy.on("SessionOpened", (model: SessionOpenedViewModel) => {
      this.emitter.emit(this.sessionOpenedReceivedEventName, model);
    });
  }

  //#endregion

  //#region SecureElement

  public retrieveSecureElement(
    clientInstanceGuid: string,
    sessionId: string,
  ): void {
    this.hubProxy.invoke(
      "RetrieveSecureElement",
      clientInstanceGuid,
      sessionId,
    );
  }

  private readonly secureElementRetrievedReceivedEventName =
    "SecureElementRetrieved";

  public subscribeSecureElementRetrievedReceived(
    method: (model: SecureElementViewModel) => void,
  ) {
    this.emitter.on(this.secureElementRetrievedReceivedEventName, method);
  }

  private registerOnServerRetrieveSecureElementEvent(): void {
    this.hubProxy.on(
      "SecureElementRetrieved",
      (model: SecureElementViewModel) => {
        this.emitter.emit(this.secureElementRetrievedReceivedEventName, model);
      },
    );
  }

  //#endregion

  //#region closedSession

  public closeSession(clientInstanceGuid: string, sessionId: string): void {
    this.hubProxy.invoke("CloseSession", clientInstanceGuid, sessionId);
  }

  private readonly sessionClosedReceivedEventName = "SessionClosed";

  public subscribeSessionClosedReceived(
    method: (model: SessionClosedViewModel) => void,
  ) {
    this.emitter.on(this.sessionClosedReceivedEventName, method);
  }

  private registerOnServerSessionClosedEvent(): void {
    this.hubProxy.on("SessionClosed", (model: SessionClosedViewModel) => {
      this.emitter.emit(this.sessionClosedReceivedEventName, model);
    });
  }

  //#endregion
}
